import "./Chart/chart.scss";
import mainStyle from "./mainStyle.module.scss";

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useOutletContext, useLocation } from "react-router-dom";
import ReactDOMServer from 'react-dom/server';
import { Box, CircularProgress } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep, isObject, orderBy } from "lodash";

import useDataSourceManager from "hooks/useDataSourceManager";
import DrawerField from "components/Drawer";

import {
  lineOption,
  deviceTypeOption,
  deviceStateOption,
  deviceStatisticOption,
} from "./const";
import Node from "./Node";
import Chart from "./Chart";
import Toolbar from "./Toolbar";
import Divider from "./Divider";
import Footer from './Footer';
import icn_refresh from "assets/images/icons/icn_refresh.svg";

import icn_button_up from "assets/images/icons/icn_button_up.svg";
import icn_button_down from "assets/images/icons/icn_button_down.svg";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const Topology = () => {
  const location = useLocation();
  const { scopeReady } = useOutletContext();
  const tooltipRef = useRef(null);
  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();

  // State
  const emptyChildrenTreesRef = useRef(null);
  const level = location.pathname.split("/")[1];
  const [auth, setAuth] = useState({
    scopeLevel: null,
    realm: null,
    orgs: null,
    sites: null,
    permissions: null,
  });
  const standalone = !auth.orgs || Object.keys(auth.orgs).length === 0;
  const [topologySocketId, _setTopologySocketId] = useState(uuidv4());
  const [regenerationSocketId] = useState(uuidv4());
  const [rawTopologyData, setRawTopologyData] = useState([]);
  const [originData, setOriginData] = useState([]);
  const [originTrees, setOriginTrees] = useState([]);
  const [trees, setTrees] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [emptyChildrenTrees, setEmptyChildrenTrees] = useState([]);
  const [emptyChildrenTreesStatistic, setEmptyChildrenTreesStatistic] = useState({});
  const [emptyChildrenTreesZoomLevel, _setEmptyChildrenTreesZoomLevel] = useState(1);
  const [selectedMacAddress, setSelectedMacAddress] = useState(null);
  const [collapsedTrees, setCollapsedTrees] = useState({});
  const [lastUpdate, setLastUpdate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [settingsData, setSettingsData] = useState(null);
  const [licenseDataLoaded, setLicenseDataLoaded] = useState(false);
  const settingsUuid = useMemo(() => uuidv4(), []);

  // Method
  const formatLastUpdate = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      let date;
      if (typeof dateTimeStr === 'number' || (typeof dateTimeStr === 'string' && /^\d+$/.test(dateTimeStr))) {
        const val = Number(dateTimeStr);
        date = new Date(val < 1e11 ? val * 1000 : val);
      } else {
        date = new Date(dateTimeStr);
      }

      if (isNaN(date.getTime())) {
        return dateTimeStr;
      }
      const yyyy = date.getFullYear();
      const mm = date.getMonth() + 1;
      const dd = date.getDate();
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
    } catch (e) {
      return dateTimeStr;
    }
  };

  const handleRefresh = () => {
    fetchLatestTopologyData();
  };

  const handleToggleCollapse = (index) => {
    setCollapsedTrees(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const fetchLicenseData = async () => {
    try {
      const realmId = auth.realm?.id || "";
      const orgId = auth.orgs?.id || "";

      const settingsApiKey = standalone
        ? "/orgs/:orgId/settings/general.read"
        : "/ents/:entId/orgs/:orgId/settings/general.read";

      const restfulParams = standalone
        ? { orgId: realmId }
        : { entId: realmId, orgId };

      const { getSendBatchData } = await sendBatch([
        {
          apiKey: settingsApiKey,
          requestUuid: settingsUuid,
          restfulPlaceholders: restfulParams,
          params: {},
        },
      ]);

      const settingsRes = getSendBatchData({ requestUuid: settingsUuid });

      if (settingsRes?.data) {
        setSettingsData(settingsRes.data);
      }
    } catch (error) {
      console.error("[Topology] Error fetching license data via sendBatch:", error);
    } finally {
      setLicenseDataLoaded(true);
    }
  };

  const traverseTree = useCallback((firstRun, rawTopologyData, parentNode, tree, statistic) => {
    tree.forEach(node => {
      // Convert to node and remove its origin data
      const properties = [];
      let deviceData = {};
      let connectionData = {}

      if (firstRun) {
        deviceData = cloneDeep({ ...node });

        for (const key in deviceData) {
          if (key !== "children") {
            properties.push(key);
          }
        }

        for (const key of properties) {
          delete node[key];
        }
      } else {
        deviceData = cloneDeep({ ...node.device });
        connectionData = cloneDeep({
          parentPort: node.parentPort ?? 0,
          childPort: node.childPort ?? 0,
          speed: node.speed ?? '',
        });

        for (const key in deviceData) {
          if (key !== "children") {
            properties.push(key);
          }
        }

        for (const key of properties) {
          delete node.device[key];
        }

        delete node.parentPort;
        delete node.childPort;
        delete node.speed;
      }

      const deviceId = deviceData.deviceId ?? uuidv4(); // Clients do not have device id
      node.id = `${deviceData.type}@${deviceId}`;
      node.originId = deviceData.deviceId ?? deviceId;
      node.parentId = parentNode ? `${parentNode.type}@${parentNode.id}` : null;
      node.originParentId = parentNode ? parentNode.id : null;
      node.type = deviceData.type;
      node.name = deviceData.name;
      node.model = deviceData.model;
      node.state = deviceData.status;
      node.mac = deviceData.macAddress;
      node.tag = deviceData.tag ? deviceData.tag.split(',') : [];
      node.wired = deviceData.wireType;
      node.children = deviceData.children;

      if (node.type !== deviceTypeOption.CLIENT) {
        node.connection = connectionData;
      }

      // Push node to rawTopologyData
      rawTopologyData.push(node);

      // Set default values
      node["remove"] = false;
      node["leading-line-color"] = `${parentNode?.id ?? ""}|${node.id}|${lineOption.COLOR}`;
      node['match'] = true;
      node["showName"] = true;
      node["showMac"] = true;

      if (node.type === deviceTypeOption.CLIENT) {
        node["leading-line-color"] = `${node.parentId}|${node.id}|${lineOption.COLOR}|${lineOption.DASH}`;
      }

      // Count devices
      if (node.type === deviceTypeOption.CLIENT) {
        switch (node.wired) {
          case deviceStatisticOption.WIRED:
            statistic[deviceStatisticOption.WIRED].count++;
            break;
          case deviceStatisticOption.WIRELESS:
            statistic[deviceStatisticOption.WIRELESS].count++;
            break;
          default:
            break;
        }
      } else {
        switch (node.state) {
          case deviceStateOption.ONLINE:
            statistic[deviceStatisticOption.ONLINE].count++;

            if (node.type === deviceTypeOption.ACCESS_POINT) {
              statistic[deviceStatisticOption.ONLINE].ap++;
            } else if (node.type === deviceTypeOption.SWITCH) {
              statistic[deviceStatisticOption.ONLINE].sw++;
            } else if (node.type === deviceTypeOption.GATEWAY) {
              statistic[deviceStatisticOption.ONLINE].gw++;
            } else if (node.type === deviceTypeOption.ROUTER) {
              statistic[deviceStatisticOption.ONLINE].rtr++;
            }

            break;
          case deviceStateOption.OFFLINE:
          case deviceStateOption.WARNING:
          case deviceStateOption.DORMANT:
            statistic[deviceStatisticOption.OFFLINE].count++;

            if (node.type === deviceTypeOption.ACCESS_POINT) {
              statistic[deviceStatisticOption.OFFLINE].ap++;
            } else if (node.type === deviceTypeOption.SWITCH) {
              statistic[deviceStatisticOption.OFFLINE].sw++;
            } else if (node.type === deviceTypeOption.GATEWAY) {
              statistic[deviceStatisticOption.OFFLINE].gw++;
            } else if (node.type === deviceTypeOption.ROUTER) {
              statistic[deviceStatisticOption.OFFLINE].rtr++;
            }

            break;
          default:
            break;
        }
      }

      // Set topic for node
      node["topic"] = ReactDOMServer.renderToStaticMarkup(
        <Node
          parentId={node.parentId}
          type={node.type}
          state={node.state}
          name={node.name}
          mac={node.mac}
          match={node.match}
          showName={node.showName}
          showMac={node.showMac}
        />
      );

      // Decide the zoom rate of chart
      statistic["maxLevel"]++;
      if (
        node.type !== deviceTypeOption.CLIENT
        && node.children?.length >= statistic["maxChildren"]
      ) {
        statistic["maxChildren"] = node.children.length;
      }

      // Collect models and tags
      if (
        node.type !== deviceTypeOption.CLIENT &&
        node.model && !statistic["models"].includes(node.model)
      ) {
        statistic["models"].push(node.model);
      }

      if (node.type !== deviceTypeOption.CLIENT && node.tag.length > 0) {
        // Only new tags will be pushed to statistic["tags"] to avoid duplication and reduce time complexity
        const newTags = node.tag.filter(tag => !statistic["tags"].includes(tag));
        statistic["tags"].push(...newTags);
      }

      // Recursive call for child nodes
      if (node.children?.length > 0) {
        traverseTree(false, rawTopologyData, node, node.children, statistic);
      }
    });

    // Remove unknown devices
    for (let index = tree.length - 1; index >= 0; index--) {
      if (tree[index]["type"] === deviceTypeOption.UNKNOWN) {
        tree.splice(index, 1);
      }
    }
  }, []);

  const filterTree = (copiedTree, statistic, filterOption, eyeOption) => {
    for (let index = 0; index < copiedTree.length; index++) {
      const node = copiedTree[index];
      const keyword = filterOption.keyword.toLowerCase();
      const model = filterOption.model.options.find(option => option.checked)?.value.toLowerCase();
      const state = filterOption.state.options.find(option => option.checked)?.value.toLowerCase();
      const tag = filterOption.tag.options.find(option => option.checked)?.value.toLowerCase();
      const keywordMatch = keyword.length === 0 ||
        node.name?.toLowerCase().includes(keyword) ||
        node.mac?.toLowerCase().includes(keyword);

      const modelMatch = !model || (
        (
          node.type === deviceTypeOption.ACCESS_POINT ||
          node.type === deviceTypeOption.SWITCH ||
          node.type === deviceTypeOption.GATEWAY ||
          node.type === deviceTypeOption.ROUTER
        ) && node.model?.toLowerCase() === model
      );

      const stateMatch = !state || node.state?.toLowerCase() === state;

      const tagMatch = !tag || node.tag.some(nodeTag => nodeTag.toLowerCase() === tag);

      node["match"] = keywordMatch && modelMatch && stateMatch && tagMatch;
      node["leading-line-color"] = `${node.parentId}|${node.id}|${lineOption.COLOR}`;
      if (node.type === deviceTypeOption.CLIENT) {
        node["leading-line-color"] = `${node.parentId}|${node.id}|${lineOption.COLOR}|${lineOption.DASH}`;
      }

      // Show or hide name and mac address
      if (node.type === deviceTypeOption.CLIENT) {
        switch (node.wired) {
          case deviceStatisticOption.WIRED:
            statistic[deviceStatisticOption.WIRED].count++;
            break;
          case deviceStatisticOption.WIRELESS:
            statistic[deviceStatisticOption.WIRELESS].count++;
            break;
          default:
            break;
        }

        node.showName = eyeOption.clientName;
        node.showMac = eyeOption.macAddress;
      } else {
        switch (node.state) {
          case deviceStateOption.ONLINE:
            statistic[deviceStatisticOption.ONLINE].count++;

            if (node.type === deviceTypeOption.ACCESS_POINT) {
              statistic[deviceStatisticOption.ONLINE].ap++;
            } else if (node.type === deviceTypeOption.SWITCH) {
              statistic[deviceStatisticOption.ONLINE].sw++;
            } else if (node.type === deviceTypeOption.GATEWAY) {
              statistic[deviceStatisticOption.ONLINE].gw++;
            } else if (node.type === deviceTypeOption.ROUTER) {
              statistic[deviceStatisticOption.ONLINE].rtr++;
            }

            break;
          case deviceStateOption.OFFLINE:
          case deviceStateOption.WARNING:
          case deviceStateOption.DORMANT:
            statistic[deviceStatisticOption.OFFLINE].count++;

            if (node.type === deviceTypeOption.ACCESS_POINT) {
              statistic[deviceStatisticOption.OFFLINE].ap++;
            } else if (node.type === deviceTypeOption.SWITCH) {
              statistic[deviceStatisticOption.OFFLINE].sw++;
            } else if (node.type === deviceTypeOption.GATEWAY) {
              statistic[deviceStatisticOption.OFFLINE].gw++;
            } else if (node.type === deviceTypeOption.ROUTER) {
              statistic[deviceStatisticOption.OFFLINE].rtr++;
            }

            break;
          default:
            break;
        }

        node.showName = eyeOption.deviceName;
        node.showMac = eyeOption.macAddress;
      }

      // Remove client nodes if they are not shown
      if (node.type === deviceTypeOption.CLIENT) {
        if (node.wired === deviceStatisticOption.WIRED && !eyeOption.wired) {
          node["remove"] = true;
        }

        if (node.wired === deviceStatisticOption.WIRELESS && !eyeOption.wireless) {
          node["remove"] = true;
        }

        if (node.state === deviceStateOption.ONLINE && !eyeOption.onlineClients) {
          node["remove"] = true;
        }

        if (node.state === deviceStateOption.OFFLINE && !eyeOption.offlineClients) {
          node["remove"] = true;
        }
      }

      // For deciding the zoom rate of chart
      statistic["maxLevel"]++;
      if (
        node.type !== deviceTypeOption.CLIENT
        && node.children.length >= statistic["maxChildren"]
      ) {
        statistic["maxChildren"] = node.children.length;
      }

      // Collect models and tags
      if (
        node.type !== deviceTypeOption.CLIENT
        && node.model && !statistic["models"].includes(node.model)
      ) {
        statistic["models"].push(node.model);
      }

      if (
        node.type !== deviceTypeOption.CLIENT
        && node.tag && !statistic["tags"].includes(node.tag)
      ) {
        statistic["tags"].push(node.tag);
      }

      // Recursive call for child nodes
      if (
        node.type !== deviceTypeOption.CLIENT
        && node.children.length > 0
      ) {
        filterTree(node.children, statistic, filterOption, eyeOption);
      }

      node["topic"] = ReactDOMServer.renderToStaticMarkup(
        <Node
          parentId={node.parentId}
          type={node.type}
          state={node.state}
          name={node.name}
          mac={node.mac}
          match={node.match}
          showName={node.showName}
          showMac={node.showMac}
        />
      );
    }

    // Remove clients if they are not shown
    for (let index = copiedTree.length - 1; index >= 0; index--) {
      if (copiedTree[index]["remove"]) {
        copiedTree.splice(index, 1);
      }
    }
  }

  const fetchData = useCallback(() => {
    // Topology data
    const updatedRawTopologyData = [];
    const updatedTrees = [];
    const updatedStatistics = [];

    // Traverse tree
    for (let i = 0; i < originData.length; i++) {
      const statistic = {
        mac: originData[i][0].macAddress,
        [deviceStatisticOption.ONLINE]: {
          count: 0,
          ap: 0,
          sw: 0,
          gw: 0,
          rtr: 0,
        },
        [deviceStatisticOption.OFFLINE]: {
          count: 0,
          ap: 0,
          sw: 0,
          gw: 0,
          rtr: 0,
        },
        [deviceStatisticOption.WIRED]: {
          count: 0,
        },
        [deviceStatisticOption.WIRELESS]: {
          count: 0,
        },
        "maxLevel": 0,
        "maxChildren": 0,
        "models": [],
        "tags": [],
      };

      traverseTree(true, updatedRawTopologyData, null, originData[i], statistic);

      updatedTrees.push(originData[i]);
      updatedStatistics.push(statistic);
    }

    setRawTopologyData(updatedRawTopologyData);

    return {
      trees: updatedTrees,
      statistics: updatedStatistics
    }
  }, [originData, traverseTree]);

  const handleSearch = (treeIndex, filterOption, eyeOption) => {
    // Normal tree
    if (treeIndex >= 0 && treeIndex < originTrees.length) {
      const copiedTree = cloneDeep(originTrees[treeIndex]);

      const statistic = {
        [deviceStatisticOption.ONLINE]: {
          count: 0,
          ap: 0,
          sw: 0,
          gw: 0,
          rtr: 0,
        },
        [deviceStatisticOption.OFFLINE]: {
          count: 0,
          ap: 0,
          sw: 0,
          gw: 0,
          rtr: 0,
        },
        [deviceStatisticOption.WIRED]: {
          count: 0,
        },
        [deviceStatisticOption.WIRELESS]: {
          count: 0,
        },
        "maxLevel": 0,
        "maxChildren": 0,
        "models": [],
        "tags": [],
      };

      filterTree(copiedTree, statistic, filterOption, eyeOption);
      let updatedTrees = cloneDeep(trees);
      updatedTrees[treeIndex] = copiedTree;
      setTrees(updatedTrees);
    }

    // Empty children tree
    if (treeIndex === -1) {
      const copiedTree = cloneDeep(emptyChildrenTrees);

      copiedTree.forEach(tree => {
        const node = tree[0];
        const keyword = filterOption.keyword.toLowerCase();
        const model = filterOption.model.options.find(option => option.checked)?.value.toLowerCase();
        const state = filterOption.state.options.find(option => option.checked)?.value.toLowerCase();
        const tag = filterOption.tag.options.find(option => option.checked)?.value.toLowerCase();

        // Check keyword
        const keywordMatch = keyword.length === 0 || node.name?.toLowerCase().includes(keyword) || node.mac?.toLowerCase().includes(keyword);

        // Check model
        const modelMatch = !model || (
          (
            node.type === deviceTypeOption.ACCESS_POINT ||
            node.type === deviceTypeOption.SWITCH ||
            node.type === deviceTypeOption.GATEWAY ||
            node.type === deviceTypeOption.ROUTER
          ) && node.model?.toLowerCase() === model
        );

        // Check state
        const stateMatch = !state || node.state?.toLowerCase() === state;

        // Check tag
        const tagMatch = !tag || node.tag.some(nodeTag => nodeTag.toLowerCase() === tag);

        // If all matches, show the node; if not, hide the node
        node["match"] = keywordMatch && modelMatch && stateMatch && tagMatch;
        node.showName = eyeOption.deviceName;
        node.showMac = eyeOption.macAddress;
      });

      setEmptyChildrenTrees(copiedTree);
    }
  }

  const handleToggleSlider = useCallback((deviceTag) => {
    if (isObject(deviceTag)) {
      setDrawerOpen(false);
      setIsLoading(true);
      sendFetchTopologyDataRequest();

      return;
    }

    if (!licenseDataLoaded) {
      return;
    }

    const macAddress = deviceTag.split('@')[1];
    setSelectedMacAddress(macAddress);
    setDrawerOpen(true);
  }, [selectedMacAddress, licenseDataLoaded]);

  const sendRegenerationRequest = () => {
    let apiKey = '/orgs/:orgId/sites/:siteId/devices/regeneration.update';
    let restfulPlaceholders = { orgId: auth.realm.id, siteId: auth.sites.id };

    if (!standalone) {
      apiKey = '/ents/:entId/orgs/:orgId/sites/:siteId/devices/regeneration.update';
      restfulPlaceholders = { entId: auth.realm.id, orgId: auth.orgs.id, siteId: auth.sites.id };
    }

    const payloadParams = {
      types: [
        {
          type: "LLDP_PORT",
          args: {}
        },
        {
          type: "FDB_TABLE",
          args: {}
        }
      ]
    };

    sendRequest(
      apiKey,
      regenerationSocketId,
      restfulPlaceholders,
      payloadParams
    );
  }

  const fetchLatestTopologyData = () => {
    setIsLoading(true);
    sendRegenerationRequest();
  }

  const sendFetchTopologyDataRequest = () => {
    let apiKey = '/orgs/sites/topology.read';
    let params = { orgId: auth.realm.id, siteId: auth.sites.id };

    if (!standalone) {
      apiKey = "/ents/orgs/sites/topology.read";
      params = { entId: auth.realm.id, orgId: auth.orgs.id, siteId: auth.sites.id };
    }

    sendRequest(
      apiKey,
      topologySocketId,
      params,
      null,
    );
  }

  // Side effect
  useEffect(() => {
    setAuth(prev => ({
      ...prev,
      scopeLevel: level,
    }));
  }, []);

  useEffect(() => {
    if (scopeReady || checkMockup === 'true') {
      const { realm, orgs, site } = JSON.parse(localStorage.getItem('selectBreadcrumbs'));
      if (isObject(site) && Object.keys(site).length > 0) {
        setAuth(prev => ({
          ...prev,
          scopeLevel: 'site',
          realm,
          orgs,
          sites: site,
          permissions: site.roleName
        }));
      } else {
        const currentPermissions = realm.roleName || orgs.roleName || '';
        setAuth(prev => ({
          ...prev,
          scopeLevel: 'org',
          realm,
          orgs,
          permissions: currentPermissions
        }));
      }
    }
  }, [scopeReady]);

  useEffect(() => {
    if (!auth.scopeLevel || !auth.realm || !auth.orgs || !auth.sites) {
      return;
    }

    setIsLoading(true);
    sendFetchTopologyDataRequest();
  }, [auth.scopeLevel, auth.realm, auth.orgs, auth.sites]);

  useEffect(() => {
    const regenerationResponse = getData(regenerationSocketId);

    if (regenerationResponse) {
      sendFetchTopologyDataRequest();
    }

    const topologyResponse = getData(topologySocketId);

    if (topologyResponse) {
      const data = topologyResponse.data ?? [];
      const updatedOriginData = [];

      for (let i = 0; i < data.length; i++) {
        updatedOriginData.push([cloneDeep(data[i])]);
      }

      setOriginData(updatedOriginData);
      if (topologyResponse.deviceInfoRegeneratedAt) {
        setLastUpdate(topologyResponse.deviceInfoRegeneratedAt);
      }
      setIsLoading(false);
      fetchLicenseData();
    }
  }, [dataMap]);

  useEffect(() => {
    if (originData.length === 0) {
      return;
    }

    const { trees: updatedTrees, statistics: updatedStatistics } = fetchData();
    for (let i = 0; i < updatedStatistics.length; i++) {
      updatedStatistics[i].models = updatedStatistics[i].models.sort((a, b) => a.localeCompare(b));
      updatedStatistics[i].tags = updatedStatistics[i].tags.sort((a, b) => a.localeCompare(b));
    }

    const sortedUpdatedStatistics = orderBy(updatedStatistics, ['maxLevel'], ['desc']);
    const sortedUpdatedOriginTrees = [];
    const sortedUpdatedTrees = [];

    for (let i = 0; i < sortedUpdatedStatistics.length; i++) {
      for (let j = 0; j < updatedTrees.length; j++) {
        if (updatedTrees[j][0].mac === sortedUpdatedStatistics[i].mac) {
          sortedUpdatedOriginTrees.push([updatedTrees[j][0]]);
          sortedUpdatedTrees.push([updatedTrees[j][0]]);
          break;
        }
      }
    }

    setOriginTrees(sortedUpdatedOriginTrees);
    setTrees(sortedUpdatedTrees);
    setStatistics(sortedUpdatedStatistics);

    // Collect empty children of trees
    const updatedEmptyChildrenTrees = updatedTrees.filter(tree => tree[0].children.length === 0);
    const updatedEmptyChildrenTreesStatistic = {
      [deviceStatisticOption.ONLINE]: {
        count: 0,
        ap: 0,
        sw: 0,
        gw: 0,
        rtr: 0,
      },
      [deviceStatisticOption.OFFLINE]: {
        count: 0,
        ap: 0,
        sw: 0,
        gw: 0,
        rtr: 0,
      },
      [deviceStatisticOption.WIRED]: {
        count: 0,
      },
      [deviceStatisticOption.WIRELESS]: {
        count: 0,
      },
      "maxLevel": 0,
      "maxChildren": 0,
      "models": new Set(),
      "tags": new Set(),
    };

    updatedEmptyChildrenTrees.forEach(tree => {
      const node = tree[0];

      switch (node.state) {
        case deviceStateOption.ONLINE:
          updatedEmptyChildrenTreesStatistic[deviceStatisticOption.ONLINE].count++;
          if (node.type === deviceTypeOption.ACCESS_POINT) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.ONLINE].ap++;
          }
          else if (node.type === deviceTypeOption.SWITCH) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.ONLINE].sw++;
          }
          else if (node.type === deviceTypeOption.GATEWAY) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.ONLINE].gw++;
          }
          else if (node.type === deviceTypeOption.ROUTER) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.ONLINE].rtr++;
          }
          break;
        case deviceStateOption.OFFLINE:
          updatedEmptyChildrenTreesStatistic[deviceStatisticOption.OFFLINE].count++;
          if (node.type === deviceTypeOption.ACCESS_POINT) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.OFFLINE].ap++;
          }
          else if (node.type === deviceTypeOption.SWITCH) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.OFFLINE].sw++;
          }
          else if (node.type === deviceTypeOption.GATEWAY) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.OFFLINE].gw++;
          }
          else if (node.type === deviceTypeOption.ROUTER) {
            updatedEmptyChildrenTreesStatistic[deviceStatisticOption.OFFLINE].rtr++;
          }
          break;
        default:
          break;
      }

      updatedEmptyChildrenTreesStatistic.models.add(node.model);
      if (node.tag.length > 0) {
        node.tag.forEach(tag => {
          updatedEmptyChildrenTreesStatistic.tags.add(tag);
        });
      }
    });

    updatedEmptyChildrenTreesStatistic.models = Array.from(updatedEmptyChildrenTreesStatistic.models).sort((a, b) => a.localeCompare(b));
    updatedEmptyChildrenTreesStatistic.tags = Array.from(updatedEmptyChildrenTreesStatistic.tags).sort((a, b) => a.localeCompare(b));

    setEmptyChildrenTrees(updatedEmptyChildrenTrees);
    setEmptyChildrenTreesStatistic(updatedEmptyChildrenTreesStatistic);

    if (!licenseDataLoaded) {
      fetchLicenseData();
    }
  }, [fetchData, originData.length, traverseTree]);

  return (
    <>
      <Box className={mainStyle["last-update-container"]}>
        <span>Last Update : {formatLastUpdate(lastUpdate)}</span>
        <button
          onClick={handleRefresh}
          className={mainStyle["refresh-button"]}
          disabled={isLoading}
          style={{ cursor: isLoading ? 'default' : 'pointer' }}
        >
          <img src={icn_refresh} alt="refresh" />
        </button>
      </Box>

      {isLoading ? (
        <Box className={mainStyle["page-loading-container"]}>
          <CircularProgress size={60} sx={{ color: "#172664" }} />
        </Box>
      ) : (
        <>
          {
            trees.map((tree, index) => {
              if (tree[0].children.length === 0) {
                return null;
              }

              const isCollapsed = collapsedTrees[index];

              return (
                <Box
                  key={`topology-${index}`}
                  className={`${mainStyle.topology} ${isCollapsed ? mainStyle.topology_collapsed : ""}`}
                >
                  <Toolbar
                    treeIndex={index}
                    statistic={statistics[index]}
                    onSearch={handleSearch}
                  />
                  <Box
                    className={mainStyle["collapse-button"]}
                    style={{ backgroundImage: `url(${isCollapsed ? icn_button_down : icn_button_up})` }}
                    onClick={() => handleToggleCollapse(index)}
                  />
                  {isCollapsed ? (
                    <Box className={mainStyle["collapse-content"]}>
                      <Node
                        parentId={tree[0].parentId}
                        type={tree[0].type}
                        state={tree[0].state}
                        name={tree[0].name}
                        mac={tree[0].mac}
                        match={tree[0].match}
                        showName={tree[0].showName}
                        showMac={tree[0].showMac}
                        tooltipRef={tooltipRef}
                        onToggleSlider={handleToggleSlider}
                        deviceId={tree[0].originId}
                        isCollapsed={isCollapsed}
                        onToggleCollapse={() => handleToggleCollapse(index)}
                      />
                    </Box>
                  ) : (
                    <Chart
                      chartId={`topology-chart-${index}`}
                      treeIndex={index}
                      rawTopologyData={rawTopologyData}
                      tree={tree}
                      statistic={statistics[index]}
                      tooltipRef={tooltipRef}
                      onToggleSlider={handleToggleSlider}
                    />
                  )}
                  <Divider />
                  <Footer />
                </Box>
              );
            })
          }
          {
            emptyChildrenTrees.length > 0 && (
              <Box
                key={`topology-empty-children`}
                className={`${mainStyle.topology} ${mainStyle['topology__empty']}`}
              >
                <Toolbar
                  treeIndex={-1}
                  statistic={emptyChildrenTreesStatistic}
                  onSearch={handleSearch}
                />
                <Box className={mainStyle["topology-title"]}>Isolated Device</Box>
                <Box
                  className={mainStyle['topology-isolated-container']}
                  style={{ transform: `scale(${emptyChildrenTreesZoomLevel})` }}
                  ref={emptyChildrenTreesRef}
                >
                  {
                    emptyChildrenTrees.map((tree, index) => (
                      <Box
                        key={`topology-empty-children-${index}`}
                        className={mainStyle['topology-empty-node']}
                      >
                        <Node
                          parentId={tree[0].parentId}
                          type={tree[0].type}
                          state={tree[0].state}
                          name={tree[0].name}
                          mac={tree[0].mac}
                          match={tree[0].match}
                          showName={tree[0].showName}
                          showMac={tree[0].showMac}
                          tooltipRef={tooltipRef}
                          onToggleSlider={handleToggleSlider}
                          deviceId={tree[0].originId}
                        />
                      </Box>

                    ))
                  }
                </Box>
                <Divider />
                <Footer treeIndex={-1} />
              </Box>
            )
          }
        </>
      )}
      {
        drawerOpen && (
          <>
            <DrawerField
              contentKey={selectedMacAddress || `Topology_${uuidv4()}`}
              open={drawerOpen}
              width="auto"
              handleClose={handleToggleSlider}
              commonContent="devices"
              contentProps={{
                fetchFlag: true,
                auth,
                deviceId: selectedMacAddress,
                settingsData,
                fetchTableData: () => {
                  sendFetchTopologyDataRequest();
                }
              }}
            />
          </>
        )
      }
    </>
  );
};

export default Topology;
